import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { of, throwError } from 'rxjs';
import { concatMap, delay, retryWhen } from 'rxjs/operators';

import { apiUrl } from '@telespot/shared/environment';
import { type RoiCropRequest } from '@analysis/use-cases';
import { AbortableHttp } from '@shared/infra/http';
import { cached } from '@shared/decorators';

/**
 * Fetches ROI crops, and only crops.
 *
 * Its own target so it can carry its own {@link ResultCache}: a crop is content-addressed
 * on the asset and the region, so no `updateRois` can make one wrong - where the ROI reads
 * share a store that every save clears.
 */
@Injectable()
export class TelespotRoiCropRepository {
  private readonly RETRY_ATTEMPTS = 3;
  private readonly RETRY_DELAY_MS = 2000;

  constructor(private readonly http: HttpClient, private readonly abortableHttp: AbortableHttp) {}

  // Keyed on the region alone: the default key is the JSON of every argument, which would
  // give one crop two entries depending on whether the caller passed a signal. An empty body
  // is not kept - an asset that was not ready yet would otherwise hold a broken cell.
  @cached({
    key: ({ assetId, coordinates }: RoiCropRequest) =>
      `${assetId}/${coordinates.x0}_${coordinates.y0}_${coordinates.x1}_${coordinates.y1}`,
    unless: (crop: Blob) => crop.size === 0,
  })
  public getBlob(request: RoiCropRequest, signal?: AbortSignal): Promise<Blob> {
    const { assetId, coordinates } = request;
    const { x0, x1, y0, y1 } = coordinates;

    const region = new HttpParams({ fromObject: { x0, x1, y0, y1 } });
    const url = `${apiUrl('mosaics')}/assets/${assetId}/crops?${region.toString()}`;
    const headers = new HttpHeaders({ Accept: 'image/jpeg, */*' });

    const request$ = this.http.get(url, { headers, responseType: 'blob' }).pipe(
      retryWhen((errors) =>
        errors.pipe(
          concatMap((error, attempt) =>
            attempt < this.RETRY_ATTEMPTS
              ? of(error).pipe(delay(this.RETRY_DELAY_MS * (attempt + 1)))
              : throwError(() => error)
          )
        )
      )
    );

    return this.abortableHttp.toPromise(request$, signal);
  }
}
